<template>
  <svg
    :width="pixelSize"
    :height="pixelSize"
    fill="none"
    :viewBox="viewBox"
    :class="mirrorClass"
    :role="role"
    :aria-hidden="ariaHidden"
    :aria-label="ariaLabel"
    focusable="false"
    xmlns="http://www.w3.org/2000/svg"
  >
    <template v-if="iconLib === 'fluent'">
      <path
        v-for="source in pathSource"
        :key="source"
        :d="source"
        fill="currentColor"
      />
    </template>
    <template v-else>
      <path
        v-for="(pathData, index) in pathSource"
        :key="index"
        :d="pathData"
        stroke="currentColor"
        :stroke-width="strokeWidth"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </template>
  </svg>
</template>
<script>
import { resolveIconSize, isDirectionalIcon, STROKE_WIDTH } from './constants';

export default {
  props: {
    icon: {
      type: String,
      required: true,
    },
    icons: {
      type: Object,
      required: true,
    },
    size: {
      type: [String, Number],
      default: '20',
    },
    type: {
      type: String,
      default: 'outline',
    },
    viewBox: {
      type: String,
      default: '0 0 24 24',
    },
    iconLib: {
      type: String,
      default: 'fluent',
    },
    /**
     * The accessible name, for an icon that carries meaning on its own — an
     * icon-only control with no visible label, or a status glyph beside a value
     * it is not merely repeating.
     *
     * Left empty (the default) the icon is decorative and hidden from assistive
     * tech, because in almost every case it sits next to the words it
     * illustrates and announcing it again is noise. Decorative is the right
     * default; a name is the deliberate exception.
     */
    label: {
      type: String,
      default: '',
    },
  },

  computed: {
    strokeWidth() {
      return STROKE_WIDTH;
    },

    pixelSize() {
      return resolveIconSize(this.size);
    },

    isDecorative() {
      return !this.label;
    },

    // Vue drops an attribute bound to null, so these three collapse to exactly
    // one of the two valid shapes: `aria-hidden="true"` with no role, or
    // `role="img"` with a name.
    role() {
      return this.isDecorative ? null : 'img';
    },

    ariaHidden() {
      return this.isDecorative ? 'true' : null;
    },

    ariaLabel() {
      return this.isDecorative ? null : this.label;
    },

    /**
     * Arabic is a daily language for this product, and a chevron that keeps
     * pointing right in it is pointing backwards. Mirroring belongs here rather
     * than at the call site: the glyph knows whether it is directional, the
     * caller does not, and 500-odd call sites cannot each be trusted to
     * remember.
     *
     * `-scale-x-100` rather than `rotate-180` — a rotation also flips the
     * vertical axis, which is invisible on a chevron but turns the send plane
     * and the diagonal expand arrow upside down.
     */
    mirrorClass() {
      return isDirectionalIcon(this.icon) ? 'rtl:-scale-x-100' : null;
    },

    pathSource() {
      // To support icons with multiple paths
      const path = this.icons[`${this.icon}-${this.type}`];

      if (path === undefined) {
        // A typo in an icon name used to throw while reading `.constructor` of
        // undefined, which takes the whole surrounding view down with it. A
        // missing glyph is a missing glyph — render nothing and say so.
        // eslint-disable-next-line no-console
        console.warn(
          `[FluentIcon] no icon named "${this.icon}-${this.type}" in the set.`
        );
        return [];
      }

      if (path.constructor === Array) {
        return path;
      }
      return [path];
    },
  },
};
</script>
